/**
 * Dragging graphics onto a sequence's graphics track: from the Graphics tab of the editor's bin, or a graphics
 * asset from the Library (in another window too: the data travels with the drag).
 */
import type { GraphicTemplateDTO } from '@prism/contracts';

export const GRAPHIC_DRAG_TYPE = 'application/x-prism-graphic';

/** A built-in template (id and version) or a graphics asset (its current template). */
export type GraphicDrop = { templateId: string; version: number } | { assetId: string };

export function setGraphicDrag(e: React.DragEvent, drop: GraphicDrop, label: string): void {
  e.dataTransfer.setData(GRAPHIC_DRAG_TYPE, JSON.stringify(drop));
  e.dataTransfer.setData('text/plain', label);
  e.dataTransfer.effectAllowed = 'copy';
}

export function readGraphicDrag(e: React.DragEvent): GraphicDrop | null {
  try {
    const raw = e.dataTransfer.getData(GRAPHIC_DRAG_TYPE);
    return raw ? (JSON.parse(raw) as GraphicDrop) : null;
  } catch {
    return null;
  }
}

/** The template a drop means: a graphics asset's newest enabled version, or the named built-in one. */
export function resolveGraphicDrop(drop: GraphicDrop, templates: GraphicTemplateDTO[]): GraphicTemplateDTO | undefined {
  if ('assetId' in drop) return templates.find((t) => t.asset?.id === drop.assetId && t.latest && t.enabled !== false);
  return templates.find((t) => t.id === drop.templateId && t.version === drop.version);
}

/** What the graphics picker offers: built-in templates and the newest version of each graphics asset still in the library. */
export const pickableTemplates = (templates: GraphicTemplateDTO[]) => templates.filter((t) => (t.asset ? t.latest && t.enabled !== false : t.enabled !== false));
